import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { selectFrReportPayload, selectIsStudySaved } from '../../state/selectors';
import { ReportContentItem } from '../../../shared/models/run.model';
import { Study } from '../../../shared/models/study.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { CalculationResult, RunStatus } from '../../../shared/models/result.model';
import { getConcentrationByRegion } from '../../../shared/utils/calibrator-helper';
import { isCalAdjustFinal } from '../../../shared/utils/run-type.util';
import { sortReportContentItemsForDisplay } from '../../../shared/utils/run-info.utils';
import { ColumnSelection } from '../../../shared/models/dialog.model';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';

type FrReportColumn = {
  key: string;
  assay: string;
  region: number | string;
  units: string;
  resultDecimalPrecision: number | string;
  userDefinedDecimalPrecision: number | string;
  resultType: string;
  items: ReportContentItem[];
};

type CalibratorSample = {
  index?: number;
  concentration?: number | string;
  meanRfi?: number | string;
  meanFR?: number | string;
};
type CalSample = {
  accessionNo?: string;
  meanRfi?: number | string;
  meanFR?: number | string;
};

@Component({
  selector: 'app-control-frreport',
  templateUrl: './control-frreport.component.html',
  styleUrl: './control-frreport.component.scss'
})
export class ControlFrreportComponent implements OnInit, OnDestroy {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  private readonly debugFrReport = true;
  isPrintView = false;
  isPrintMode = false;
  studyName = '';
  panelName = '';
  specVersion = '';
  appVersion = '';
  runType = '';
  testSpec = '';
  operatorName = '';
  columnsAll: FrReportColumn[] = [];
  columns: FrReportColumn[] = [];
  tableColumns: ColumnSelection[] = [];
  selectedColumns: ColumnSelection[] = [];
  calibratorRows: number[] = [1, 2, 3, 4, 5];
  /**
   * "User Defined Decimal Precision" is the widest label on the page; the data columns hold one
   * number each. Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 56, columnWidthMm: 24 };

  trackByColumn = (_index: number, column: FrReportColumn) => column.key;
  trackByPage = (index: number) => index;

  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  private study: Study | null = null;
  private reportContentItems: ReportContentItem[] = [];
  private calculationResult: CalculationResult | null | undefined;
  private calLots: CalLot[] = [];
  private calibratorDataByItemId = new Map<string, Map<number, CalibratorSample>>();
  private calSamplesByItemId = new Map<string, Map<string, CalSample>>();
  private runStatusByItemId = new Map<string, RunStatus>();
  private assignmentByRegion = new Map<number, Map<number, number | string>>();
  private regionRows = new Map<number, Array<{ barcode: string; concentration: number | string }>>();
  private destroy$ = new Subject<void>();

  constructor(private store: Store, private router: Router) { }

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;
    this.store.select(selectFrReportPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ study, reportContentResponse, appState }) => {
        this.study = study;
        this.studyName = study?.studyName ?? '';
        this.panelName = study?.panelSpecs?.kitname ?? '';
        this.specVersion = study?.panelSpecs?.revisionNumber ?? '';
        this.appVersion = appState?.appVersion ?? '';
        this.runType = study?.runType ?? '';
        this.testSpec = study?.testSpec ?? '';
        this.operatorName = appState?.loggedInUser ?? '';
        this.reportContentItems = sortReportContentItemsForDisplay(
          reportContentResponse?.reportContentItems ?? [],
          study?.runType
        );
        // A Final run has no baseline, so its results take the original's place.
        this.calculationResult = isCalAdjustFinal(study)
          ? study?.calculationAdjustedResponse?.calculationResponse
          : study?.calculationResponse?.calculationResponse;
        this.calLots = study?.calLots ?? [];

        this.runStatusByItemId = new Map(
          (this.calculationResult?.runStatus ?? []).map(run => [
            String(run.reportContentItemId ?? ''),
            run
          ])
        );
        this.calibratorDataByItemId.clear();
        this.calSamplesByItemId.clear();
        this.regionRows.clear();
        this.assignmentByRegion = this.buildAssignmentByRegion();
        this.calibratorRows = this.buildCalibratorRows();
        this.columnsAll = this.filterRenderableColumns(this.buildColumns());
        this.columns = this.columnsAll;
        // Re-key any print selection onto the rebuilt columns. The store hands back every column
        // selected, so without this a rebuild — which the audit write on Print itself can cause —
        // would send the whole table to the printer however few columns were ticked.
        this.tableColumns = applyPrintColumnSelection(
          this.buildTableColumns(this.columnsAll),
          this.printColumnSelection
        );
        this.handleSelectColumns(this.tableColumns);
        this.logFrReportDebugSnapshot();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCalibratorAssignment(column: FrReportColumn, level: number): string {
    const regionKey = this.toRegionNumber(column.region);
    const assignmentMap = regionKey !== null ? this.assignmentByRegion.get(regionKey) : undefined;
    const directValue = assignmentMap?.get(level);
    if (directValue !== undefined) {
      return this.formatValue(directValue);
    }
    const sample = this.getFirstSampleForLevel(column, level);
    return this.formatValue(sample?.concentration);
  }

  getAverageRfi(column: FrReportColumn, level: number): string {
    return this.formatAverageByBarcode(column, level, 'meanRfi');
  }

  getAverageFr(column: FrReportColumn, level: number): string {
    return this.formatAverageByBarcode(column, level, 'meanFR');
  }

  get printPages(): FrReportColumn[][] {
    return chunkColumnsForPrint(this.columns, this.printPageFit);
  }

  /** "Columns 6-10 of 22 · page 2 of 5" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }

  navigateToPrintView(): void {
    this.router.navigateByUrl('/(print:print/preview-fr-report)');
  }

  handleSelectColumns(selectedColumns: ColumnSelection[]): void {
    this.selectedColumns = selectedColumns;
    const selectedKeys = new Set(
      selectedColumns.filter(col => col.isSelected).map(col => col.columnKey ?? col.columnName)
    );
    this.columns = this.columnsAll.filter(column => selectedKeys.has(this.buildColumnKey(column)));
  }

  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumns(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.runType,
      this.testSpec ? `Test Spec ${this.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  private buildColumns(): FrReportColumn[] {
    const columns: FrReportColumn[] = [];
    const seen = new Map<string, FrReportColumn>();

    this.reportContentItems.forEach(item => {
      const assay = item.assay ?? '';
      const region = item.region ?? '';
      const key = `${assay}__${region}`;
      const existing = seen.get(key);
      if (existing) {
        existing.items.push(item);
        return;
      }

      const column: FrReportColumn = {
        key,
        assay,
        region,
        units: item.units ?? '',
        resultDecimalPrecision: item.resultDecimalPrecision ?? '',
        userDefinedDecimalPrecision: item.userDefinedDecimalPrecision ?? '',
        resultType: item.resultType ?? '',
        items: [item]
      };
      seen.set(key, column);
      columns.push(column);
    });

    return columns;
  }

  private filterRenderableColumns(columns: FrReportColumn[]): FrReportColumn[] {
    return columns.filter(column => this.columnHasRenderableData(column));
  }

  private columnHasRenderableData(column: FrReportColumn): boolean {
    return this.calibratorRows.some(level => (
      this.getCalibratorAssignment(column, level) !== '-' ||
      this.getAverageRfi(column, level) !== '-' ||
      this.getAverageFr(column, level) !== '-'
    ));
  }

  private buildTableColumns(columns: FrReportColumn[]): ColumnSelection[] {
    return columns.map(column => ({
      columnName: `${column.assay} ${column.region}`.trim(),
      columnKey: this.buildColumnKey(column),
      assayName: column.assay,
      beadRegion: column.region,
      isSelected: true
    }));
  }

  private buildColumnKey(column: FrReportColumn): string {
    return `${column.assay ?? ''}||${column.region ?? ''}`;
  }

  private getFirstSampleForLevel(column: FrReportColumn, level: number): CalibratorSample | undefined {
    const firstItem = column.items[0];
    if (!firstItem) {
      return undefined;
    }
    const map = this.getCalibratorMap(firstItem);
    return map.get(level);
  }

  private formatAverageByBarcode(column: FrReportColumn, level: number, field: keyof CalSample): string {
    const row = this.getRegionRow(column, level);
    if (!row) {
      return '-';
    }
    const values: number[] = [];
    column.items.forEach(item => {
      const map = this.getCalSamplesMap(item);
      const sample = map.get(row.barcode);
      const numeric = this.toNumber(sample?.[field]);
      if (numeric !== null) {
        values.push(numeric);
      }
    });

    if (!values.length) {
      return '-';
    }

    const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
    return this.formatNumber(avg);
  }

  private getCalibratorMap(item: ReportContentItem): Map<number, CalibratorSample> {
    const id = String(item.reportContentItemId ?? '');
    const existing = this.calibratorDataByItemId.get(id);
    if (existing) {
      return existing;
    }

    const vials = this.calLots?.[0]?.vials ?? [];
    const runStatus = this.runStatusByItemId.get(id);
    const calibratorData = getConcentrationByRegion(Number(item.region), runStatus, vials) as CalibratorSample[];
    const map = new Map<number, CalibratorSample>();
    calibratorData.forEach(sample => {
      if (typeof sample.index === 'number') {
        map.set(sample.index, sample);
      }
    });
    this.calibratorDataByItemId.set(id, map);
    return map;
  }

  private getCalSamplesMap(item: ReportContentItem): Map<string, CalSample> {
    const id = String(item.reportContentItemId ?? '');
    const existing = this.calSamplesByItemId.get(id);
    if (existing) {
      return existing;
    }
    const runStatus = this.runStatusByItemId.get(id);
    const samples = (runStatus?.calCurveInformation?.calSamples ?? []) as CalSample[];
    const map = new Map<string, CalSample>();
    samples.forEach(sample => {
      const key = sample?.accessionNo ? String(sample.accessionNo) : '';
      if (key) {
        map.set(key, sample);
      }
    });
    this.calSamplesByItemId.set(id, map);
    return map;
  }

  private buildAssignmentByRegion(): Map<number, Map<number, number | string>> {
    const result = new Map<number, Map<number, number | string>>();
    const vials = this.calLots?.[0]?.vials ?? [];

    vials.forEach((vial, vialIndex) => {
      const rowNumber = vialIndex + 1;
      (vial.levels ?? []).forEach(level => {
        const region = this.toRegionNumber(level.region);
        if (region === null) {
          return;
        }

        const map = result.get(region) ?? new Map<number, number | string>();
        map.set(rowNumber, level.concentration);
        result.set(region, map);

        const rows = this.regionRows.get(region) ?? [];
        rows[vialIndex] = { barcode: vial.barcode, concentration: level.concentration };
        this.regionRows.set(region, rows);
      });
    });

    return result;
  }

  private buildCalibratorRows(): number[] {
    let maxRow = 0;
    this.regionRows.forEach(rows => {
      if (rows.length > maxRow) {
        maxRow = rows.length;
      }
    });

    if (maxRow <= 0) {
      maxRow = 5;
    }

    return Array.from({ length: maxRow }, (_val, idx) => idx + 1);
  }

  private getRegionRow(column: FrReportColumn, level: number): { barcode: string; concentration: number | string } | undefined {
    const regionKey = this.toRegionNumber(column.region);
    if (regionKey === null) {
      return undefined;
    }
    const rows = this.regionRows.get(regionKey) ?? [];
    return rows[level - 1];
  }

  private toRegionNumber(value: number | string | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }
    const fixed = value.toFixed(4);
    return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }

  private formatValue(value: unknown): string {
    const numeric = this.toNumber(value);
    if (numeric === null) {
      return '-';
    }
    return this.formatNumber(numeric);
  }

  private logFrReportDebugSnapshot(): void {
    if (!this.debugFrReport) {
      return;
    }

    const vialSummary = (this.calLots?.[0]?.vials ?? []).map((vial, vialIndex) => ({
      row: vialIndex + 1,
      barcode: vial.barcode,
      levels: (vial.levels ?? []).map(level => ({
        region: level.region,
        concentration: level.concentration
      }))
    }));

    const assignmentByRegion = Array.from(this.assignmentByRegion.entries()).map(([region, map]) => ({
      region,
      rows: Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([row, concentration]) => ({ row, concentration }))
    }));

    const regionRows = Array.from(this.regionRows.entries()).map(([region, rows]) => ({
      region,
      rows: rows.map((row, index) => ({
        row: index + 1,
        barcode: row?.barcode,
        concentration: row?.concentration
      }))
    }));

    const columnSummary = this.columnsAll.map(column => ({
      key: column.key,
      assay: column.assay,
      region: column.region,
      reportContentItemIds: column.items.map(item => item.reportContentItemId)
    }));

    const matrix = this.columnsAll.map(column => ({
      key: column.key,
      region: column.region,
      assignment: this.calibratorRows.map(level => ({ level, value: this.getCalibratorAssignment(column, level) })),
      avgRfi: this.calibratorRows.map(level => ({ level, value: this.getAverageRfi(column, level) })),
      avgFr: this.calibratorRows.map(level => ({ level, value: this.getAverageFr(column, level) }))
    }));

    console.group('[FR Report Debug] Snapshot');
    console.log('[FR Report Debug] study', {
      studyName: this.studyName,
      panelName: this.panelName,
      runType: this.runType,
      testSpec: this.testSpec,
      specVersion: this.specVersion,
      totalReportContentItems: this.reportContentItems.length,
      totalColumns: this.columnsAll.length,
      calibratorRows: this.calibratorRows
    });
    console.log('[FR Report Debug] vialSummary', vialSummary);
    console.log('[FR Report Debug] assignmentByRegion', assignmentByRegion);
    console.log('[FR Report Debug] regionRows', regionRows);
    console.log('[FR Report Debug] columnSummary', columnSummary);
    console.log('[FR Report Debug] valueMatrix', matrix);
    console.groupEnd();
  }
}
